import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, useHistory } from 'react-router-dom'
import { notification } from 'antd'

/**
 * ProtectedRoute Component - Prevents Direct URL Access
 *
 * Checks if user has required permissions BEFORE rendering a page.
 * If user doesn't have permission, shows a top-right error notification
 * and redirects back to the root (which routes them appropriately).
 * If user isn't logged in, redirects to login.
 *
 * Usage in routes.js:
 *   {
 *     path: '/contracts/edit/:id',
 *     Component: loadable(() => import('pages/contracts/edit')),
 *     exact: true,
 *     requiredPermissions: [PERMISSIONS.CONTRACT.EDIT], // ALL must be present
 *     anyPermissions: [PERMISSIONS.CUSTOMER.VIEW, PERMISSIONS.CUSTOMER.DASHBOARD], // ANY ONE must be present
 *   }
 *
 * Then in router.js wrap routes with ProtectedRoute
 */
const ProtectedRoute = ({ path, Component, requiredPermissions, anyPermissions, exact, ...rest }) => {
  const { authorized, permissions } = useSelector(state => ({
    authorized: state.user.authorized,
    permissions: state.user.permissions || [],
  }))

  const hasSlug = slug => permissions.some(p => (typeof p === 'string' ? p : p?.slug) === slug)

  const hasRequiredPermissions = () => {
    // ALL of requiredPermissions must be present (if any specified)
    const allPass =
      !requiredPermissions ||
      requiredPermissions.length === 0 ||
      requiredPermissions.every(hasSlug)

    // At least ONE of anyPermissions must be present (if any specified)
    const anyPass =
      !anyPermissions ||
      anyPermissions.length === 0 ||
      anyPermissions.some(hasSlug)

    return allPass && anyPass
  }

  // 1. User not logged in → Redirect to login
  if (!authorized) {
    return <Redirect to="/system/login" />
  }

  // 2. Missing permissions → toast + redirect to root (RootRedirect picks the right destination)
  if (!hasRequiredPermissions()) {
    return <AccessDenied path={path} />
  }

  // 3. All checks passed → Render the component
  return <Component {...rest} />
}

/**
 * Renders nothing, fires a top-right error notification, then navigates to root.
 * Kept as a separate component so useEffect fires exactly once per denial.
 */
const AccessDenied = ({ path }) => {
  const history = useHistory()

  useEffect(() => {
    notification.error({
      message: 'Access Denied',
      description: 'You do not have permission to view this page.',
    })
    console.warn(`Access Denied: User lacks required permissions for ${path}`)
    history.goBack()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}

export default ProtectedRoute
